import { useEffect, useState } from 'react';
import { corsMediaUrl } from '@/lib/corsMedia';

/** Export image format. Cards are fully opaque, so JPEG is safe (no alpha needed). */
export type ExportFormat = 'png' | 'jpeg';
export const JPEG_QUALITY = 0.9;
export const exportMime = (f: ExportFormat) => (f === 'jpeg' ? 'image/jpeg' : 'image/png');
export const exportExt = (f: ExportFormat) => (f === 'jpeg' ? 'jpg' : 'png');
export const exportQuality = (f: ExportFormat) => (f === 'jpeg' ? JPEG_QUALITY : undefined);

/** A rectangle in logical (design) pixels. */
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Placement of a photo inside its frame: top-left in logical px + uniform scale. */
export interface PhotoTransform {
  x: number;
  y: number;
  scale: number;
}

export const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/** Load a URL into an HTMLImageElement, re-rendering when it's ready. */
export function useHtmlImage(src: string | null): HTMLImageElement | null {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!src) {
      setImg(null);
      return;
    }
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    let active = true;
    image.onload = () => {
      if (active) setImg(image);
    };
    // Own cache entry for the CORS variant — see corsMediaUrl.
    image.src = corsMediaUrl(src);
    return () => {
      active = false;
    };
  }, [src]);
  return img;
}

/** Uniform scale + centered offset that makes an image fully cover a frame. */
export function coverTransform(imgW: number, imgH: number, frame: Rect): PhotoTransform {
  const scale = Math.max(frame.width / imgW, frame.height / imgH);
  return {
    scale,
    x: frame.x + (frame.width - imgW * scale) / 2,
    y: frame.y + (frame.height - imgH * scale) / 2,
  };
}

/**
 * Clamp per axis: when the image is larger than the frame on that axis, keep it
 * covering (no gaps); when smaller, keep it inside the frame (so it can sit with
 * margin around it, e.g. a logo). Used where the image may be scaled below cover.
 */
export function clampInFrame(
  x: number,
  y: number,
  imgW: number,
  imgH: number,
  scale: number,
  frame: Rect,
): { x: number; y: number } {
  const dispW = imgW * scale;
  const dispH = imgH * scale;
  const cx =
    dispW >= frame.width
      ? clamp(x, frame.x + frame.width - dispW, frame.x)
      : clamp(x, frame.x, frame.x + frame.width - dispW);
  const cy =
    dispH >= frame.height
      ? clamp(y, frame.y + frame.height - dispH, frame.y)
      : clamp(y, frame.y, frame.y + frame.height - dispH);
  return { x: cx, y: cy };
}

/** Clamp a photo's top-left so the scaled image always fully covers the frame. */
export function clampToCover(
  x: number,
  y: number,
  imgW: number,
  imgH: number,
  scale: number,
  frame: Rect,
): { x: number; y: number } {
  const dispW = imgW * scale;
  const dispH = imgH * scale;
  return {
    x: clamp(x, frame.x + frame.width - dispW, frame.x),
    y: clamp(y, frame.y + frame.height - dispH, frame.y),
  };
}

/**
 * Vertical nudge (logical px, positive = down) to move text so its actual glyph
 * box is centered, instead of the font's em box. Konva's verticalAlign="middle"
 * centers the em box, which leaves all-caps text sitting visibly high (the unused
 * descender space pushes the visual centre down). Add this to the text node's y.
 */
export function centerNudge(
  ctx: CanvasRenderingContext2D,
  text: string,
  size: number,
  fontFamily: string,
  weight = 400,
): number {
  // Konva's verticalAlign="middle" places the font's "middle" baseline at the
  // box centre. Measure the actual glyph ink box relative to that same middle
  // baseline; the offset of the ink centre from it is exactly how far the text
  // sits off-centre. Shift down by that amount to centre the glyphs.
  ctx.font = `${weight} ${size}px "${fontFamily}", sans-serif`;
  ctx.textBaseline = 'middle';
  const m = ctx.measureText(text || 'X');
  const asc = m.actualBoundingBoxAscent ?? size * 0.35;
  const desc = m.actualBoundingBoxDescent ?? size * 0.35;
  // Ink centre relative to the middle baseline is (desc - asc)/2 (down positive);
  // move the text down by its negation to bring the ink centre onto the baseline.
  return (asc - desc) / 2;
}

/**
 * Fit text into a fixed panel: word-wrap at the design size first, then shrink
 * the font only if the wrapped lines still overflow the box.
 */
export function fitFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  box: { width: number; height: number },
  baseFontSize: number,
  fontFamily: string,
  opts: { lineHeight?: number; padding?: number; min?: number; weight?: number } = {},
): number {
  const lineHeight = opts.lineHeight ?? 1.05;
  const padding = opts.padding ?? 6;
  const min = opts.min ?? 12;
  const weight = opts.weight ?? 400;
  const maxW = box.width - padding * 2;
  const maxH = box.height - padding * 2;
  const words = text.split(/\s+/).filter(Boolean);
  const fits = (size: number) => {
    ctx.font = `${weight} ${size}px "${fontFamily}", sans-serif`;
    let lines = 1;
    let widest = 0;
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (!cur || ctx.measureText(test).width <= maxW) {
        cur = test;
      } else {
        widest = Math.max(widest, ctx.measureText(cur).width);
        lines += 1;
        cur = w;
      }
    }
    widest = Math.max(widest, ctx.measureText(cur).width);
    return widest <= maxW && lines * size * lineHeight <= maxH;
  };
  let size = baseFontSize;
  while (size > min && !fits(size)) size -= 1;
  return size;
}
